/**
 * Reusable workout editor modal (warmup + circuits + finisher).
 * Used by Admin Content (Workouts tab) and Admin Program Form (edit assigned workouts).
 */
import React, { useState, useEffect } from 'react';
import { updateWorkoutTemplate, deleteWorkoutTemplate } from '../utils/adminData';
import '../styles/Admin.css';

export const WORKOUT_PROGRAM_OPTIONS = [
  { value: 'volleyball', label: 'Volleyball' },
  { value: 'multi_sport_sprint', label: 'Multi-Sport Sprint' },
  { value: 'shared', label: 'Shared' },
];

export const EMPTY_WORKOUT = {
  id: '',
  name: '',
  type: 'strength',
  program: null,
  estimated_duration_minutes: 55,
  warmup_template_id: '',
  finisher_template_id: '',
  circuits: [],
};

export function blocksToDraft(blocks) {
  const warmup = (blocks || []).find((b) => b.block_type === 'warmup');
  const main = (blocks || []).find((b) => b.block_type === 'main');
  const finisher = (blocks || []).find((b) => b.block_type === 'finisher');
  return {
    warmup_template_id: warmup?.template_id || '',
    finisher_template_id: finisher?.template_id || '',
    circuits: (main?.circuits || []).map((c) => ({
      circuit_name: c.circuit_name || '',
      rounds: c.rounds ?? 3,
      rest_between_exercises: c.rest_between_exercises ?? 60,
      rest_after_circuit: c.rest_after_circuit ?? 90,
      exercises: (c.exercises || []).map((e) => ({
        exercise_id: e.exercise_id || '',
        reps: e.reps ?? '',
        intensity: e.intensity || '',
        notes: e.notes || '',
      })),
    })),
  };
}

function draftToBlocks(draft) {
  const blocks = [];
  if (draft.warmup_template_id) {
    blocks.push({ block_type: 'warmup', template_id: draft.warmup_template_id });
  }
  if ((draft.circuits || []).length > 0) {
    blocks.push({
      block_type: 'main',
      circuits: (draft.circuits || []).map((c) => ({
        circuit_name: c.circuit_name || 'Circuit',
        rounds: c.rounds ?? 3,
        rest_between_exercises: c.rest_between_exercises ?? 60,
        rest_after_circuit: c.rest_after_circuit ?? 90,
        exercises: (c.exercises || []).map((e) => ({
          exercise_id: e.exercise_id || '',
          reps: String(e.reps ?? ''),
          intensity: e.intensity || '',
          notes: e.notes || '',
        })),
      })),
    });
  }
  if (draft.finisher_template_id) {
    blocks.push({ block_type: 'finisher', template_id: draft.finisher_template_id });
  }
  return blocks;
}

function AdminWorkoutEditorModal({
  item,
  isAddMode = false,
  warmupsList = [],
  finishersList = [],
  exercisesList = [],
  defaultProgramId,
  programOptions = WORKOUT_PROGRAM_OPTIONS,
  onSave,
  onClose,
  onDelete,
  showDelete = true,
}) {
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    if (isAddMode) {
      setDraft({
        ...EMPTY_WORKOUT,
        program: defaultProgramId || null,
      });
    } else if (item) {
      const base = blocksToDraft(item.blocks || []);
      setDraft({
        id: item.id,
        name: item.name || '',
        type: item.type || 'strength',
        program: item?.program ?? defaultProgramId ?? null,
        estimated_duration_minutes: item.estimated_duration_minutes ?? 55,
        ...base,
      });
    } else {
      setDraft(null);
    }
    setSaveError('');
    setDeleteError('');
  }, [item, isAddMode, defaultProgramId]);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const addCircuit = () => {
    setDraft((d) => ({
      ...d,
      circuits: [...(d.circuits || []), { circuit_name: 'New circuit', rounds: 3, rest_between_exercises: 60, rest_after_circuit: 90, exercises: [] }],
    }));
  };

  const updateCircuit = (index, field, value) => {
    setDraft((d) => ({
      ...d,
      circuits: (d.circuits || []).map((c, i) => (i === index ? { ...c, [field]: value } : c)),
    }));
  };

  const removeCircuit = (index) => {
    setDraft((d) => ({ ...d, circuits: (d.circuits || []).filter((_, i) => i !== index) }));
  };

  const addExercise = (circuitIndex) => {
    setDraft((d) => {
      const circuits = [...(d.circuits || [])];
      const c = circuits[circuitIndex] || { circuit_name: '', rounds: 3, exercises: [] };
      circuits[circuitIndex] = { ...c, exercises: [...(c.exercises || []), { exercise_id: '', reps: '8-10', intensity: 'moderate', notes: '' }] };
      return { ...d, circuits };
    });
  };

  const updateExercise = (circuitIndex, exIndex, field, value) => {
    setDraft((d) => {
      const circuits = (d.circuits || []).map((c, ci) => {
        if (ci !== circuitIndex) return c;
        return { ...c, exercises: (c.exercises || []).map((e, ei) => (ei === exIndex ? { ...e, [field]: value } : e)) };
      });
      return { ...d, circuits };
    });
  };

  const removeExercise = (circuitIndex, exIndex) => {
    setDraft((d) => {
      const circuits = (d.circuits || []).map((c, ci) => {
        if (ci !== circuitIndex) return c;
        return { ...c, exercises: (c.exercises || []).filter((_, ei) => ei !== exIndex) };
      });
      return { ...d, circuits };
    });
  };

  const handleDelete = async () => {
    const id = item?.id;
    if (!id) return;
    if (!window.confirm(`Delete workout "${item?.name || id}"? This cannot be undone.`)) return;
    setDeleting(true);
    setDeleteError('');
    setSaveError('');
    try {
      await deleteWorkoutTemplate(id);
      if (typeof onDelete === 'function') await onDelete();
      onClose();
    } catch (err) {
      console.error('[AdminWorkoutEditorModal] Delete failed:', err);
      setDeleteError(err?.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const saveEdit = async () => {
    if (!draft?.id?.trim()) {
      setSaveError('ID is required (e.g. multi_sport_m1_day_a)');
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      const payload = {
        id: draft.id.trim(),
        name: draft.name || draft.id,
        type: draft.type || 'strength',
        program: draft.program === 'shared' ? null : (draft.program || null),
        estimated_duration_minutes: draft.estimated_duration_minutes ?? 55,
        blocks: draftToBlocks(draft),
      };
      await updateWorkoutTemplate(draft.id.trim(), payload);
      if (typeof onSave === 'function') await onSave();
      onClose();
    } catch (err) {
      setSaveError(err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!draft) return null;

  return (
    <div
      className="admin-content-edit-modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="workout-editor-modal-title"
    >
      <div className="admin-content-edit-modal admin-content-edit-modal--workout" onClick={(e) => e.stopPropagation()}>
        <header className="admin-content-edit-modal-header">
          <h2 id="workout-editor-modal-title" className="admin-content-edit-modal-title">
            {isAddMode ? 'Add new workout' : `Edit: ${item?.name || draft.name}`}
          </h2>
          <button type="button" className="admin-content-edit-modal-close" onClick={onClose} aria-label="Close">×</button>
        </header>
        <div className="admin-content-edit-modal-body">
          <div className="admin-workout-builder">
            <div className="admin-catalog-edit-fields admin-workout-builder-top">
              <div className="input-float-wrapper admin-workout-builder-top-program">
                <select
                  className="input"
                  value={draft.program ?? 'shared'}
                  onChange={(e) => setDraft((d) => ({ ...d, program: e.target.value }))}
                >
                  {programOptions.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
                <label className="input-float-label">Program</label>
              </div>
              <div className="input-float-wrapper">
                {isAddMode ? (
                  <input type="text" className="input" placeholder=" " value={draft.id || ''} onChange={(e) => setDraft((d) => ({ ...d, id: e.target.value }))} />
                ) : (
                  <input type="text" className="input" placeholder=" " value={draft.id || ''} readOnly />
                )}
                <label className="input-float-label">ID</label>
              </div>
              <div className="input-float-wrapper">
                <input type="text" className="input" placeholder=" " value={draft.name || ''} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} />
                <label className="input-float-label">Name</label>
              </div>
              <div className="input-float-wrapper">
                <input type="number" className="input" min={1} placeholder=" " value={draft.estimated_duration_minutes ?? ''} onChange={(e) => setDraft((d) => ({ ...d, estimated_duration_minutes: parseInt(e.target.value, 10) || 55 }))} />
                <label className="input-float-label">Duration (min)</label>
              </div>
            </div>

            <div className="admin-workout-builder-block">
              <label className="admin-form-label">Warmup</label>
              <div className="input-float-wrapper">
                <select
                  className="input"
                  value={draft.warmup_template_id || ''}
                  onChange={(e) => setDraft((d) => ({ ...d, warmup_template_id: e.target.value }))}
                >
                  <option value="">— None —</option>
                  {(warmupsList || []).map((w) => (
                    <option key={w.id} value={w.id}>{w.name || w.id}</option>
                  ))}
                </select>
                <label className="input-float-label">Select Warmup</label>
              </div>
            </div>

            <div className="admin-workout-builder-block">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-3)' }}>
                <label className="admin-form-label">Main (circuits)</label>
                <button type="button" className="btn btn--secondary btn--sm" onClick={addCircuit}>+ Add circuit</button>
              </div>
              {(draft.circuits || []).map((circuit, ci) => (
                <div key={ci} className="admin-workout-builder-circuit" style={{ marginBottom: 'var(--space-4)', padding: 'var(--space-4)', background: 'var(--color-bg-elevated)', borderRadius: 'var(--radius)' }}>
                  <div className="admin-form-row admin-workout-circuit-row">
                    <div className="input-float-wrapper admin-workout-builder-circuit-name">
                      <input type="text" className="input" placeholder=" " value={circuit.circuit_name || ''} onChange={(e) => updateCircuit(ci, 'circuit_name', e.target.value)} />
                      <label className="input-float-label">Circuit</label>
                    </div>
                    <div className="input-float-wrapper admin-workout-circuit-rnds">
                      <input type="number" className="input" min={1} placeholder=" " value={circuit.rounds ?? ''} onChange={(e) => updateCircuit(ci, 'rounds', parseInt(e.target.value, 10) || 3)} />
                      <label className="input-float-label">RNDs</label>
                    </div>
                    <div className="input-float-wrapper admin-workout-circuit-rest-btw">
                      <input type="number" className="input" min={0} placeholder=" " value={circuit.rest_between_exercises ?? ''} onChange={(e) => updateCircuit(ci, 'rest_between_exercises', parseInt(e.target.value, 10) || 60)} />
                      <label className="input-float-label">Rest btw (s)</label>
                    </div>
                    <div className="input-float-wrapper admin-workout-circuit-rest-post">
                      <input type="number" className="input" min={0} placeholder=" " value={circuit.rest_after_circuit ?? ''} onChange={(e) => updateCircuit(ci, 'rest_after_circuit', parseInt(e.target.value, 10) || 90)} />
                      <label className="input-float-label">Rest post (s)</label>
                    </div>
                    <button type="button" className="btn btn--ghost btn--sm" onClick={() => removeCircuit(ci)} aria-label="Remove circuit">×</button>
                  </div>
                  <div style={{ marginTop: 'var(--space-2)' }}>
                    {(circuit.exercises || []).map((ex, ei) => (
                      <div key={ei} className="admin-form-row admin-workout-exercise-row">
                        <div className="input-float-wrapper admin-workout-exercise-select">
                          <select
                            className="input input--sm"
                            value={ex.exercise_id || ''}
                            onChange={(e) => updateExercise(ci, ei, 'exercise_id', e.target.value)}
                          >
                            <option value="">— Exercise —</option>
                            {(exercisesList || []).map((e) => (
                              <option key={e.id} value={e.id}>{e.name || e.id}</option>
                            ))}
                          </select>
                          <label className="input-float-label">Exercise</label>
                        </div>
                        <div className="input-float-wrapper admin-workout-reps">
                          <input type="text" className="input input--sm" placeholder=" " value={ex.reps ?? ''} onChange={(e) => updateExercise(ci, ei, 'reps', e.target.value)} />
                          <label className="input-float-label">Reps</label>
                        </div>
                        <div className="input-float-wrapper admin-workout-intensity">
                          <input type="text" className="input input--sm" placeholder=" " value={ex.intensity ?? ''} onChange={(e) => updateExercise(ci, ei, 'intensity', e.target.value)} />
                          <label className="input-float-label">Intensity</label>
                        </div>
                        <button type="button" className="btn btn--ghost btn--sm" onClick={() => removeExercise(ci, ei)}>×</button>
                      </div>
                    ))}
                    <button type="button" className="btn btn--secondary btn--sm" onClick={() => addExercise(ci)}>+ Add exercise</button>
                  </div>
                </div>
              ))}
            </div>

            <div className="admin-workout-builder-block">
              <label className="admin-form-label">Finisher</label>
              <div className="input-float-wrapper">
                <select
                  className="input"
                  value={draft.finisher_template_id || ''}
                  onChange={(e) => setDraft((d) => ({ ...d, finisher_template_id: e.target.value }))}
                >
                  <option value="">— None —</option>
                  {(finishersList || []).map((f) => (
                    <option key={f.id} value={f.id}>{f.name || f.id}</option>
                  ))}
                </select>
                <label className="input-float-label">Select Finisher</label>
              </div>
            </div>
          </div>
        </div>
        <footer className="admin-content-edit-modal-footer">
          {showDelete && !isAddMode && (
            <button type="button" className="btn btn--secondary btn--sm admin-content-delete-btn" onClick={handleDelete} disabled={saving || deleting} style={{ marginRight: 'auto' }}>
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          )}
          {(saveError || deleteError) && <span style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-sm)', marginRight: 'auto' }}>{saveError || deleteError}</span>}
          <button type="button" className="btn btn--secondary btn--sm" onClick={onClose} disabled={saving || deleting}>Cancel</button>
          <button type="button" className="btn btn--primary btn--sm" onClick={saveEdit} disabled={saving || deleting}>{saving ? 'Saving…' : 'Save'}</button>
        </footer>
      </div>
    </div>
  );
}

export default AdminWorkoutEditorModal;
